// Copyright (c) 2012-2021 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved. NOTICE: All information contained herein
// is, and remains the property of GRAB. The intellectual and technical concepts contained herein are confidential, proprietary
// and controlled by GRAB and may be covered by patents, patents in process, and are protected by trade secret or copyright law.
//
// You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate, adapt or change this material
// in any way unless prior written permission is obtained from GRAB. Access to the source code contained herein is hereby
// forbidden to anyone except current GRAB employees or contractors with binding Confidentiality and Non-disclosure agreements
// explicitly covering such access.
//
// The copyright notice above does not evidence any actual or intended publication or disclosure of this source code,
// which includes information that is confidential and/or proprietary, and is a trade secret, of GRAB.
//
// ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR THROUGH USE OF THIS SOURCE
// CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE LAWS AND
// INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY
// OR IMPLY ANY RIGHTS TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING
// THAT IT MAY DESCRIBE, IN WHOLE OR IN PART.

import React from 'react'
import {
  Button,
  Select,
  Input,
  Anchor,
  Space,
  Row,
  Col,
  Radio,
  Switch,
  message,
  Modal,
  Typography,
  Cascader
} from 'antd'
import {
  EnvironmentOutlined,
  CameraOutlined,
  CloudUploadOutlined,
  BarChartOutlined,
  ForkOutlined
} from '@ant-design/icons'
import './index.less'
import { RootState, Dispatch } from 'store'
import TimePicker from 'components/time-picker'
import { connect } from 'react-redux'
import { SettingCard } from 'components/setting-card'
import { getGeneralSetting, updateGeneralSetting } from 'api/setting'
import { ComposedTitle } from 'components/composed-title'
import { ISettingValues, SettingModel } from 'models/setting-model'
import {
  IntervalType,
  VideoResolution,
  ImageryResolution,
  UploadMethod,
  ImageryFormat,
  UploadFormat,
  ImageCompression,
  TimeBasedIntervalRange,
  DistanceBasedIntervalrange,
  GPSUploadIntervalRange,
  IMUUploadIntervalRange, MonthlyDataQuotaRange
} from 'constants/setting'
import { Prompt } from 'react-router-dom'
import {
  TimeBasedIntervalOptions,
  DistanceBasedIntervalOptions,
  UploadIntervalOptions,
  DataQuataOptions,
  AlertThresholdOptions,
  HardThresholdOptions,
  MonitorDashboard
} from 'components/general-setting'
import { FirmwareVersionCell } from 'components/FirmwareVersionCell'
import { Moment } from 'moment'
import { SupportedAIModels } from '../../constants/AIModels'
import { formatNumberInput } from 'utils/format'
import { buildCascaderData, filter } from '../../components/CityFilter'

function mapState (rootState: RootState) {
  return {
    cities: rootState.city.cityList,
    defaultCity: rootState.city.current,
    firmwareState: rootState.firmware
  }
}

function mapDispatch (dispatch: Dispatch) {
  return {
    firmwareAction: dispatch.firmware
  }
}

export type IGeneralSettingProps = ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>

export type IGeneralSettingState = {
  setting: ISettingValues
  dirty: boolean
  cityID: number
  isCityEnable: boolean
  saving: boolean
  continent: string
  country: string
}

const aiTitle = (
  <div>
    <Space>
      AI Models
      <MonitorDashboard />
    </Space>
  </div>
)

class GeneralSetting extends React.Component<IGeneralSettingProps, IGeneralSettingState> {
  private main = React.createRef<HTMLDivElement>()
  private settingModel: SettingModel

  constructor (props) {
    super(props)
    this.createSettingModel()
    this.state = {
      ...this.settingModel.getState(),
      cityID: -1,
      continent: '',
      country: '',
      isCityEnable: false,
      saving: false,
    }
  }

  private bindScrollContainer = () => {
    return this.main.current || window
  }

  componentDidMount () {
    const { firmwareAction, defaultCity } = this.props
    const {continent, country} = defaultCity
    this.setCity(defaultCity.id)
    this.setState({continent,country})
    firmwareAction.fetchLatestVersion().catch(console.log)
  }

  componentWillUnmount () {
    this.settingModel.dispose()
  }

  private handleStateChange = state => {
    this.setState(state)
  }

  private handleReset = () => {
    this.settingModel.reset()
  }

  private setCity = (cityID: number) => {
    const oldCityID = this.state.cityID
    this.setState(
      {
        cityID: cityID
      },
      async () => {
        if (oldCityID === cityID) {
          return
        }
        try {
          const setting = await getGeneralSetting(cityID)
          if (setting) {
            this.createSettingModel(setting)
            this.setState({
              ...this.settingModel.getState(),
              isCityEnable: true
            })
          }
        } catch (error) {
          if (error.response.status === 404) {
            Modal.error({
              title: '',
              content: 'This city is not enabled'
            })
          } else {
            message.error(error.toString())
          }
          this.setState({
            isCityEnable: false
          })
        }
      }
    )
  }

  private handleCityChange = value => {
    const lastIndex = value.length > 0 ? value.length - 1 : 0
    let newCityID = value[lastIndex]
    const [continent,country] = value
    const oldCityID = this.state.cityID
    if (this.settingModel.isDirty()) {
      let confirmChanged = window.confirm('You are leaving the page without saving')
      newCityID = confirmChanged ? value : oldCityID
    }
    this.setCity(newCityID)
    this.setState({continent, country})
  }

  private createSettingModel (values?) {
    if (this.settingModel) {
      this.settingModel.dispose()
    }
    this.settingModel = new SettingModel(values)
    this.settingModel.emitter.on('change', this.handleStateChange)
  }

  private handleSettingChange = (key, value) => {
    switch (key) {
      case 'imageryFmt':
        if (value === true) {
          value = ImageryFormat.H256
        } else {
          value = ImageryFormat.JPG
        }
        break
      case 'trafficQuota':
        value = value !== '' ? parseInt(value) : MonthlyDataQuotaRange[0]
        if (value > MonthlyDataQuotaRange[1]) {
          value = MonthlyDataQuotaRange[1]
        }
        if (value < MonthlyDataQuotaRange[0]) {
          value = MonthlyDataQuotaRange[0]
        }
        break
      case 'gpsUploadInterval':
        value = value !== '' ? parseInt(value) : GPSUploadIntervalRange[0]
        if (value > GPSUploadIntervalRange[1]) {
          value = GPSUploadIntervalRange[1]
        }
        if (value < GPSUploadIntervalRange[0]) {
          value = GPSUploadIntervalRange[0]
        }
        break
      case 'imuUploadInterval':
        value = value !== '' ? parseInt(value) : IMUUploadIntervalRange[0]
        if (value > IMUUploadIntervalRange[1]) {
          value = IMUUploadIntervalRange[1]
        }
        if (value < IMUUploadIntervalRange[0]) {
          value = IMUUploadIntervalRange[0]
        }
        break
      case 'captureInterval':
        value = value !== '' ? parseInt(value) : TimeBasedIntervalRange[0]
        if (value > TimeBasedIntervalRange[1]) {
          value = TimeBasedIntervalRange[1]
        }
        if (value < TimeBasedIntervalRange[0]) {
          value = TimeBasedIntervalRange[0]
        }
        break
      case 'captureDistanceInterval':
        value = value !== '' ? parseInt(value) : DistanceBasedIntervalrange[0]
        if (value > DistanceBasedIntervalrange[1]) {
          value = DistanceBasedIntervalrange[1]
        }
        if (value < DistanceBasedIntervalrange[0]) {
          value = DistanceBasedIntervalrange[0]
        }
        break
      default:
        break
    }

    this.settingModel.set(key, value)
  }

  private handleTimeChange = (values: Moment[]) => {
    if (values[0].isSame(values[1])) {
      return
    }
    this.settingModel.set('workTimeStart', values[0])
    this.settingModel.set('workTimeEnd', values[1])
  }

  private handleSave = async () => {
    this.setState({
      saving: true
    })
    const values = this.settingModel.buildUpdateParam(this.settingModel.getDirtySettings())
    try {
      await updateGeneralSetting(this.state.cityID, values)
      this.createSettingModel(this.settingModel.getState().setting)
      this.settingModel.emitter.emit('change', this.settingModel.getState())
      message.success('Changes has been successfully saved')
    } catch (error) {
      Modal.error({
        title: 'Error',
        content: error.toString()
      })
    } finally {
      this.setState({
        saving: false
      })
    }
  }

  private handleSaveConfirm = () => {
    Modal.confirm({
      title: null,
      content: 'Are you sure you want to save these changes?',
      onOk: () => {
        return new Promise((resolve, reject) => {
          resolve(this.handleSave())
        })
      },
      okText: 'Yes',
      cancelText: 'No'
    })
  }

  render () {
    const { cities, firmwareState } = this.props
    const { setting, dirty, isCityEnable, cityID, continent,country } = this.state
    const disabled = !isCityEnable
    const options = buildCascaderData(cities)
    return (
      <div className='general-setting'>
        <div className='aside'>
          <div className='aside-title'>General Settings</div>
          <div className='aside-inner'>
            <div className='city-selection'>
              <Space size={16}>
                <EnvironmentOutlined className='icon' />
                <Cascader
                  style={{ width: 208 }}
                  allowClear={false}
                  onChange={this.handleCityChange}
                  value={[continent, country,cityID === -1 ? null : cityID]}
                  displayRender={(values)=> values[values.length > 0 ? values.length -1 : 0 ]}
                  placeholder='input the cameras city'
                  expandTrigger={'hover'}
                  showSearch={{filter}}
                  options={options}
                />
              </Space>
            </div>
            <div className='menu'>
              <Anchor getContainer={this.bindScrollContainer} onClick={e => e.preventDefault()}>
                <Anchor.Link
                  href='#camera'
                  title={
                    <Space size={12}>
                      <CameraOutlined />
                      Camera
                    </Space>
                  }
                />
                <Anchor.Link
                  href='#imageryUpload'
                  title={
                    <Space size={12}>
                      <CloudUploadOutlined />
                      Imagery Upload
                    </Space>
                  }
                />
                <Anchor.Link
                  href='#aiModels'
                  title={
                    <Space size={12}>
                      <ForkOutlined />
                      AI Models
                    </Space>
                  }
                />
                <Anchor.Link
                  href='#dataUsage'
                  title={
                    <Space size={12}>
                      <BarChartOutlined />
                      Data Quota
                    </Space>
                  }
                />
              </Anchor>
            </div>
            <div className='aside-rest'>
              {/* <Space size={12}>
                <FileTextOutlined />
                View Settings History
              </Space> */}
            </div>
            {dirty && (
              <div className='aside-action'>
                <div style={{ color: '#818181', fontSize: 10, marginBottom: 6 }}>
                  * Changes will only be synced when camera is online.
                </div>
                <Button type='primary' onClick={this.handleSaveConfirm}>
                  Save
                </Button>
                <Button onClick={this.handleReset}>Reset Changes</Button>
              </div>
            )}
          </div>
        </div>
        <div className='main' ref={this.main}>
          <SettingCard title='Camera' icon={<CameraOutlined />} className='setting-item' id='camera'>
            <SettingCard.Row>
              <Row justify='space-between' align='middle'>
                <Col>Power Savings Mode</Col>
                <Col>
                  <Switch
                    disabled={disabled}
                    checked={setting.powerSave}
                    onChange={v => this.handleSettingChange('powerSave', v)}
                  />
                </Col>
              </Row>
            </SettingCard.Row>
            <SettingCard.Row>
              <Row justify='start' align='middle'>
                <Col span={4}>Firmware</Col>
                <Col>
                  <FirmwareVersionCell
                    version={firmwareState.latestVersoin}
                    loading={firmwareState.latestVersionLoading}
                  />
                </Col>
              </Row>
            </SettingCard.Row>
            <SettingCard.Row className='padding-v-10'>
              <Row justify='space-between' align='middle'>
                <Col span={16}>
                  <ComposedTitle
                    primary='Camera Recording Hours'
                    secondary='Set the time when camera recording features are enabled. Recording will not be allowed outside of this hours to protect user’s privacy.'
                    secondaryStyle={{ whiteSpace: 'normal' }}
                  />
                </Col>
                <Col>
                  {
                    //@ts-ignore
                    <TimePicker.RangePicker
                      disabled={disabled}
                      style={{ width: 195 }}
                      value={[setting.workTimeStart, setting.workTimeEnd]}
                      onChange={this.handleTimeChange}
                      format='HH:mm'
                    />
                  }
                </Col>
              </Row>
            </SettingCard.Row>
            <SettingCard.CollapseRow
              title={
                <ComposedTitle
                  primary='Continuous Capture Settings'
                  secondary='Set how cameras should capture images either in time-based or distance based intervals.'
                />
              }
              defaultCollapsed={true}
              headerClassName='no-border-b'
            >
              <div>Choose One:</div>
              <Radio.Group
                style={{ padding: '16px 0px 0px 24px', width: '100%' }}
                value={setting.intervalType}
                onChange={e => this.handleSettingChange('intervalType', e.target.value)}
                disabled={disabled}
              >
                <Row align='middle' justify={'space-between'} style={{ marginBottom: 16 }} gutter={20}>
                  <Col>
                    <Radio value={IntervalType.Time}>Time-Based Interval:</Radio>
                  </Col>
                  <Col>
                    <Input
                      style={{ width: 195 }}
                      type='number'
                      disabled={disabled || setting.intervalType === IntervalType.Distance}
                      prefix={<span style={{marginRight: 10}}>Every</span>}
                      suffix={<span style={{ marginLeft: 10 }}>s</span>}
                      min={TimeBasedIntervalRange[0]}
                      max={TimeBasedIntervalRange[1]}
                      onKeyDown={formatNumberInput}
                      value={setting.captureInterval}
                      placeholder='Input a number'
                      onChange={e => this.handleSettingChange('captureInterval', e.target.value)}
                    />
                  </Col>
                </Row>
                <Row align='middle' justify={'space-between'} gutter={20}>
                  <Col>
                    <Radio value={IntervalType.Distance}>Distance-Based Interval:</Radio>
                  </Col>
                  <Col>
                    <Input
                      style={{ width: 195 }}
                      type='number'
                      disabled={disabled || setting.intervalType === IntervalType.Time}
                      prefix={<span style={{marginRight: 10}}>Every</span>}
                      suffix={<span style={{ marginLeft: 10 }}>m</span>}
                      min={DistanceBasedIntervalrange[0]}
                      max={DistanceBasedIntervalrange[1]}
                      onKeyDown={formatNumberInput}
                      value={setting.captureDistanceInterval}
                      placeholder='Input a number'
                      onChange={e => this.handleSettingChange('captureDistanceInterval', e.target.value)}
                    />
                  </Col>
                </Row>
              </Radio.Group>
            </SettingCard.CollapseRow>
          </SettingCard>
          <SettingCard
            title='Imagery Upload'
            icon={<CloudUploadOutlined />}
            className='setting-item'
            id='imageryUpload'
          >
            <SettingCard.CollapseRow title='Upload Format and Quality' defaultCollapsed={true}>
              <div>Choose One:</div>
              <Radio.Group
                style={{ padding: '16px 0px 16px 0px', width: '100%' }}
                value={setting.uploadFmt}
                onChange={e => this.handleSettingChange('uploadFmt', e.target.value)}
                disabled={disabled}
              >
                <Row align='middle' style={{ marginBottom: 16 }} gutter={20}>
                  <Col span={3}>
                    <Radio value={UploadFormat.Video}>Video:</Radio>
                  </Col>
                  <Col>
                    <Select
                      style={{ width: 166 }}
                      value={setting.videoResolution}
                      onChange={v => this.handleSettingChange('videoResolution', v)}
                      disabled={disabled || setting.uploadFmt === UploadFormat.Images}
                    >
                      <Select.Option value={VideoResolution._720}>720p</Select.Option>
                      <Select.Option value={VideoResolution._1080}>1080p</Select.Option>
                      <Select.Option value={VideoResolution._2700}>2.7k</Select.Option>
                      <Select.Option value={VideoResolution._4000}>4k</Select.Option>
                    </Select>
                  </Col>
                </Row>
                <Row align='middle' gutter={20}>
                  <Col span={3}>
                    <Radio value={UploadFormat.Images}>Images:</Radio>
                  </Col>
                  <Col>
                    <Select
                      style={{ width: 166 }}
                      value={setting.imageryResolution}
                      onChange={v => this.handleSettingChange('imageryResolution', v)}
                      disabled={disabled || setting.uploadFmt === UploadFormat.Video}
                    >
                      <Select.Option value={ImageryResolution._2_MP}>2MP</Select.Option>
                      <Select.Option value={ImageryResolution._5_MP}>5MP</Select.Option>
                      <Select.Option value={ImageryResolution._8_MP}>8MP</Select.Option>
                      <Select.Option value={ImageryResolution._12_MP}>12MP</Select.Option>
                    </Select>
                  </Col>

                  {setting.uploadFmt === UploadFormat.Images && (
                    <Col style={{ marginLeft: 'auto' }}>
                      <span className='ant-radio-wrapper' style={{ marginRight: 16 }}>
                        Image Quality:
                      </span>
                      <Select
                        style={{ width: 110 }}
                        value={setting.imageCompressRatio}
                        onChange={v => this.handleSettingChange('imageCompressRatio', v)}
                      >
                        <Select.Option value={ImageCompression.Standard}>Standard</Select.Option>
                        <Select.Option value={ImageCompression.High}>High</Select.Option>
                      </Select>
                    </Col>
                  )}
                </Row>
              </Radio.Group>
            </SettingCard.CollapseRow>
            <SettingCard.Row>
              <Row justify='space-between' align='middle'>
                <Col>
                  <ComposedTitle
                    primary='Data Saver Mode'
                    secondary='Encode multiple JPEGs into a single MP4 file to optimize files size. Requires more processing power.'
                  />
                </Col>
                <Col>
                  <Switch
                    checked={setting.imageryFmt === ImageryFormat.H256}
                    disabled={disabled}
                    onChange={v => this.handleSettingChange('imageryFmt', v)}
                  />
                </Col>
              </Row>
            </SettingCard.Row>
            <SettingCard.Row className='padding-v-10'>
              <Row justify='space-between' align='middle'>
                <Col>Upload Method</Col>
                <Col>
                  <Select
                    style={{ width: 195 }}
                    value={setting.uploadMethod}
                    onChange={v => this.handleSettingChange('uploadMethod', v)}
                    disabled={disabled}
                  >
                    <Select.Option value={UploadMethod._4G}>4G</Select.Option>
                    <Select.Option value={UploadMethod.Wifi}>Wifi</Select.Option>
                    <Select.Option value={UploadMethod.Wifi_4G}>4G + Wifi</Select.Option>
                  </Select>
                </Col>
              </Row>
            </SettingCard.Row>
            <SettingCard.Row className='padding-v-10'>
              <Row justify='space-between' align='middle'>
                <Col>GPS Upload Interval</Col>
                <Col>
                  <Input
                    style={{ width: 195 }}
                    type='number'
                    disabled={disabled}
                    prefix={<span style={{marginRight: 10}}>Every</span>}
                    suffix={<span style={{ marginLeft: 10 }}>s</span>}
                    min={GPSUploadIntervalRange[0]}
                    max={GPSUploadIntervalRange[1]}
                    onKeyDown={formatNumberInput}
                    value={setting.gpsUploadInterval}
                    placeholder='Input a number'
                    onChange={e => this.handleSettingChange('gpsUploadInterval', e.target.value)}
                  />
                </Col>
              </Row>
            </SettingCard.Row>
            <SettingCard.Row className='padding-v-10'>
              <Row justify='space-between' align='middle'>
                <Col>IMU Upload Interval</Col>
                <Col>
                  <Input
                    style={{ width: 195 }}
                    type='number'
                    disabled={disabled}
                    prefix={<span style={{marginRight: 10}}>Every</span>}
                    suffix={<span style={{ marginLeft: 10 }}>s</span>}
                    min={IMUUploadIntervalRange[0]}
                    max={IMUUploadIntervalRange[1]}
                    onKeyDown={formatNumberInput}
                    value={setting.imuUploadInterval}
                    placeholder='Input a number'
                    onChange={e => this.handleSettingChange('imuUploadInterval', e.target.value)}
                  />
                </Col>
              </Row>
            </SettingCard.Row>
          </SettingCard>
          <SettingCard title={aiTitle} icon={<ForkOutlined />} className='setting-item' id='aiModels'>
            {SupportedAIModels.map(({ name, key }) => (
              <SettingCard.Row className='padding-v-10' key={key}>
                <Row justify='space-between' align='middle'>
                  <Col>
                    <div>{name}</div>
                    {/*<Typography.Text type='secondary'>Latest Version: 1.235 </Typography.Text>*/}
                    {/*<Button type='link'>update</Button>*/}
                  </Col>
                  <Col>
                    <Switch
                      checked={setting[key]}
                      disabled={disabled}
                      onChange={checked => this.handleSettingChange(key, checked)}
                    />
                  </Col>
                </Row>
              </SettingCard.Row>
            ))}
          </SettingCard>
          <SettingCard title='Data Quota' icon={<BarChartOutlined />} className='setting-item' id='dataUsage'>
            <SettingCard.Row>
              <Row justify='space-between' align='middle'>
                <Col>
                  <ComposedTitle
                    primary='Monthly Data Quota:'
                    secondary={`Quota usage limitation (max ${MonthlyDataQuotaRange[1]})`}
                  />
                </Col>
                <Col>
                  <Input
                    style={{ width: 165 }}
                    type='number'
                    min={MonthlyDataQuotaRange[0]}
                    max={MonthlyDataQuotaRange[1]}
                    onKeyDown={formatNumberInput}
                    value={setting.trafficQuota}
                    placeholder='Input a number'
                    onChange={v => this.handleSettingChange('trafficQuota', v.target.value)}
                  />
                  <span style={{ marginLeft: 10 }}>GB</span>
                </Col>
              </Row>
            </SettingCard.Row>
            <SettingCard.Row>
              <Row justify='space-between' align='middle'>
                <Col>
                  <ComposedTitle primary='Data Alert Range:' secondary='Warn me when a camera is within this range.' />
                </Col>
                <Col>
                  <Select
                    style={{ width: 74 }}
                    value={setting.alertThreshold}
                    onChange={v => this.handleSettingChange('alertThreshold', v)}
                    disabled={disabled}
                  >
                    {AlertThresholdOptions}
                  </Select>
                  <span style={{ marginLeft: 10 }}>of monthly quota</span>
                </Col>
              </Row>
            </SettingCard.Row>
            <SettingCard.Row>
              <Row justify='space-between' align='middle'>
                <Col>
                  <ComposedTitle
                    primary='Data Hard Limit:'
                    secondary='Stop all upload from a camera within this range.'
                  />
                </Col>
                <Col>
                  <Select
                    style={{ width: 74 }}
                    value={setting.hardThreshold}
                    onChange={v => this.handleSettingChange('hardThreshold', v)}
                    disabled={disabled}
                  >
                    {HardThresholdOptions}
                  </Select>
                  <span style={{ marginLeft: 10 }}>of monthly quota</span>
                </Col>
              </Row>
            </SettingCard.Row>
          </SettingCard>
          <div style={{ height: 800 }} />
        </div>
        <Prompt when={dirty} message='You are leaving the page without saving' />
      </div>
    )
  }
}

export default connect(mapState, mapDispatch)(GeneralSetting)
